import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useCallback, useRef } from 'react';

import { messagesQueryKeys } from '../../../hooks/use-messages';
import { api } from '../../../lib/api-client';

import type {
  AttachedDocument,
  ListMessagesResponse,
  MessageItem,
  SendMessageBody,
  SendMessageEnvelope,
} from '../../../lib/types';
import type { InfiniteData, UseMutationResult } from '@tanstack/react-query';

/**
 * Item 236 (29/Mai/2026) — hints opcionais para o optimistic update.
 *
 * `attachedDocument` é injetado no `MessageItem` otimista para que o card de
 * anexo apareça na bubble outbound ANTES de o servidor responder. signedUrl
 * fica `undefined` no optimistic — chega via `onSuccess` (envelope.message
 * já contém a signedUrl real, que substitui o optimistic no cache).
 */
export interface SendMessageOptimisticOpts {
  attachedDocument?: AttachedDocument;
}

/**
 * useSendMessage — Fatia 6 PR-A + Sprint Backend Protection v1 Agent C (Item 226).
 *
 * Mutation otimista: mensagem aparece imediatamente no histórico com
 * `status: 'sending'`. Servidor confirma → `setQueryData` substitui pelo
 * Message real (status `sent`). MockOutboundService emite WebSocket
 * `conversation.message.delivery.updated` → `useConversationEvents`
 * invalida query e re-fetch atualiza `delivered` / `read`.
 *
 * Idempotência: gera `Idempotency-Key` localmente (ulid simulado via timestamp
 * + random) por tentativa de envio. Retry manual reusa a mesma chave →
 * servidor detecta duplicata e retorna mesmo Message sem efeito colateral.
 *
 * Em falha (rede, 403, 409, 422), mensagem otimista vira `failed` + retry
 * disponível via `retry()`.
 *
 * Sprint Backend Protection v1 (Item 226) + F1.3 (bloqueio honesto):
 *   - Response canonical é envelope `SendMessageEnvelope` ({message, validation?}).
 *   - `validation` exposta via mutation.data?.validation para o Composer renderizar
 *     ProtectionWarningBanner (severity warn) inline.
 *   - Em 400 com `validation_blocked`, ApiError chega via mutation.error e o
 *     Composer abre ProtectionBlockModal (informativo) lendo `error.body.validation`.
 *   - F1.3: NÃO há reenvio com override — o bloqueio é HARD no Engine. O
 *     caminho `sendAcknowledged`/`X-Protection-Acknowledged` foi removido.
 */
export interface SendMessageVariables {
  body: SendMessageBody;
  /** Chave gerada localmente para optimistic update. */
  optimisticId: string;
  /** Idempotency-Key enviada ao servidor. */
  idempotencyKey: string;
  /** Item 236 — hint para optimistic; NÃO é enviado ao servidor. */
  attachedDocument?: AttachedDocument;
}

export interface UseSendMessageResult {
  send: (
    body: SendMessageBody,
    opts?: SendMessageOptimisticOpts,
  ) => Promise<SendMessageEnvelope | null>;
  retry: (optimisticId: string) => Promise<SendMessageEnvelope | null>;
  mutation: UseMutationResult<SendMessageEnvelope, Error, SendMessageVariables>;
}

export function useSendMessage(
  conversationId: string | undefined,
  currentUserId: string | undefined,
): UseSendMessageResult {
  const queryClient = useQueryClient();
  // Mantém últimas mensagens otimistas em memória para suportar retry.
  const optimisticBacklogRef = useRef<Map<string, SendMessageVariables>>(new Map());

  const mutation = useMutation<SendMessageEnvelope, Error, SendMessageVariables>({
    mutationFn: async (vars) => {
      if (conversationId === undefined) {
        throw new Error('conversationId obrigatório para sendMessage');
      }
      return api.sendMessage(conversationId, vars.body, vars.idempotencyKey);
    },

    onMutate: (vars) => {
      if (conversationId === undefined) return;
      optimisticBacklogRef.current.set(vars.optimisticId, vars);

      const optimisticMessage: MessageItem = {
        id: vars.optimisticId,
        conversationId,
        direction: 'outbound',
        origin: 'attendant',
        content: vars.body.content,
        contentType: vars.body.contentType ?? 'text',
        metadata: vars.body.metadata ?? {},
        status: 'sending',
        senderUserId: currentUserId ?? null,
        failureReason: null,
        sentAt: new Date().toISOString(),
        deliveredAt: null,
        readAt: null,
        // Item 236 — card de anexo aparece no bubble outbound otimista; sem
        // signedUrl (chega via envelope no onSuccess + refetch).
        ...(vars.attachedDocument !== undefined && { attachedDocument: vars.attachedDocument }),
      };

      // Insere no cache do useInfiniteQuery (página 0 = mais recente) — nas
      // DUAS chaves (F.5 C1 do Alvo D: com o fio default ON, a chave
      // person-scope é o caminho principal e setQueryData não faz prefix-match).
      for (const key of messagesQueryKeys(conversationId)) {
        queryClient.setQueryData<InfiniteData<ListMessagesResponse>>(key, (old) => {
          if (!old) return old;
          const [first, ...rest] = old.pages;
          if (!first) return old;
          const newFirst: ListMessagesResponse = {
            data: [optimisticMessage, ...first.data],
            meta: first.meta,
          };
          return { ...old, pages: [newFirst, ...rest] };
        });
      }
    },

    onSuccess: (envelope, vars) => {
      if (conversationId === undefined) return;
      // Substitui mensagem otimista pelo Message real — nas duas chaves.
      for (const key of messagesQueryKeys(conversationId)) {
        queryClient.setQueryData<InfiniteData<ListMessagesResponse>>(key, (old) => {
          if (!old) return old;
          return {
            ...old,
            pages: old.pages.map((page) => ({
              ...page,
              data: page.data.map((m) => (m.id === vars.optimisticId ? envelope.message : m)),
            })),
          };
        });
      }
      optimisticBacklogRef.current.delete(vars.optimisticId);
      // Invalida conversation list (badge + ordenação atualizam).
      void queryClient.invalidateQueries({ queryKey: ['conversations'] });
    },

    onError: (_err, vars) => {
      if (conversationId === undefined) return;
      // Marca optimistic como failed (mantém em backlog para retry).
      // Sprint Protection v1: em ApiError 400 'validation_blocked', Composer
      // captura `_err` (ApiError) via mutation.error e abre ProtectionBlockModal.
      for (const key of messagesQueryKeys(conversationId)) {
        queryClient.setQueryData<InfiniteData<ListMessagesResponse>>(key, (old) => {
          if (!old) return old;
          return {
            ...old,
            pages: old.pages.map((page) => ({
              ...page,
              data: page.data.map((m) =>
                m.id === vars.optimisticId
                  ? { ...m, status: 'failed' as const, failureReason: 'send-failed' }
                  : m,
              ),
            })),
          };
        });
      }
    },
  });

  const send = useCallback(
    async (
      body: SendMessageBody,
      opts?: SendMessageOptimisticOpts,
    ): Promise<SendMessageEnvelope | null> => {
      if (conversationId === undefined) return null;
      const optimisticId = `optimistic_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;
      const idempotencyKey = optimisticId; // mesma chave para retry usar
      const result = await mutation.mutateAsync({
        body,
        optimisticId,
        idempotencyKey,
        ...(opts?.attachedDocument !== undefined && { attachedDocument: opts.attachedDocument }),
      });
      return result;
    },
    [conversationId, mutation],
  );

  const retry = useCallback(
    async (optimisticId: string): Promise<SendMessageEnvelope | null> => {
      const original = optimisticBacklogRef.current.get(optimisticId);
      if (!original) return null;
      // Reseta status para 'sending' antes de re-tentar.
      for (const key of messagesQueryKeys(conversationId ?? '')) {
        queryClient.setQueryData<InfiniteData<ListMessagesResponse>>(key, (old) => {
          if (!old) return old;
          return {
            ...old,
            pages: old.pages.map((page) => ({
              ...page,
              data: page.data.map((m) =>
                m.id === optimisticId
                  ? { ...m, status: 'sending' as const, failureReason: null }
                  : m,
              ),
            })),
          };
        });
      }
      return mutation.mutateAsync(original);
    },
    [conversationId, mutation, queryClient],
  );

  return { send, retry, mutation };
}
